/**
 * React Query hook for Dashboard Statistics
 * 
 * Fetches aggregated stats from the API for the dashboard.
 * Stats are scoped to the current tenant.
 */

import { useQuery } from '@tanstack/react-query';
import apiClient from '@/lib/api-client';
import { useTenant } from '@/contexts/TenantContext';

// =============================================================================
// Types
// =============================================================================

export interface AgentStats {
  total: number;
  active: number;
  draft: number;
}

export interface CollectionStats {
  total: number;
  documents: number;
}

export interface ConversationStats {
  total: number;
  messages: number;
}

export interface AgentUsage {
  agentName: string;
  conversations: number;
  messages: number;
  toolCalls: number;
}

export interface ActivityItem {
  id: string;
  type: string;
  description: string;
  timestamp: string;
  agentName?: string;
  userName?: string;
}

export interface DashboardStats {
  agents: AgentStats;
  collections: CollectionStats;
  conversations: ConversationStats;
  users: { total: number };
  groups: { total: number };
  agentUsage?: AgentUsage[];
  recentActivity?: ActivityItem[];
}

// =============================================================================
// Query Key
// =============================================================================

const STATS_KEY = 'dashboard-stats';

// =============================================================================
// Hook
// =============================================================================

/**
 * Fetch dashboard statistics for the current tenant
 * Returns null data if no tenant is selected
 */
export function useStats() {
  const { currentTenant } = useTenant();
  const tenantId = currentTenant?.id;

  return useQuery({
    queryKey: [STATS_KEY, tenantId],
    queryFn: async () => {
      if (!tenantId) {
        throw new Error('No tenant selected');
      }
      const { data } = await apiClient.get<DashboardStats>('/api/v1/stats');
      return data;
    },
    enabled: !!tenantId, // Only run query if tenant is selected
    staleTime: 30 * 1000, // 30 seconds for more frequent updates
    refetchInterval: 30 * 1000, // Refetch every 30 seconds
  });
}
